/* eslint-disable react/jsx-props-no-spreading */
import { Button, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  API_ADVERTISERS_EDIT_URL,
  API_CLIENTS_ADVERTISERS_CREATE_URL,
  API_USERS_MANAGERS_LIST_URL,
  API_USERS_SALES_LIST_URL,
  API_USERS_TRAFFICKER_LIST_URL,
} from '../../constants/apiUrls';
import { CLIENTS_ADVERTISERS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useSnackBar } from '../../hooks/useSnackBar';
import { isAchHost } from '../../util/achAuth';
import { unwrapAchBareCollection, unwrapAchShowModel } from '../../util/achEnvelope';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import CreativesList from '../Creatives/CreativesList';
import Category from '../Creatives/forms/fields/Category';
import FormEndOptions from '../forms/FormEndOptions';
import Select from '../forms/Select';
import Switch from '../forms/Switch';
import TextField from '../forms/TextField';

interface ClientsAdvertisersFormInputs {
  id?: number;
  clientId: number;
  name?: string;
  managerId: number;
  salesRepId: number;
  traffickerId: number;
  mapsEnabled: boolean;
  category: string;
  subcategory: string;
}

export const defaultValues: ClientsAdvertisersFormInputs = {
  id: undefined,
  clientId: null,
  name: '',
  managerId: null,
  salesRepId: null,
  traffickerId: null,
  mapsEnabled: false,
  category: null,
  subcategory: null,
};

const normalizePayload = (clientId, form) => {
  const returnValue = {
    ...snakeCaseKeys({
      ...form,
      clientId,
    }),
    timeStamp: new Date().getTime(),
  };

  return returnValue;
};

const normalizeForm = (data) => {
  const returnValue = {
    ...camelCaseKeys(data),
  };

  return returnValue;
};

interface ClientsAdvertisersFormProps {
  id?: number;
  clientId?: number;
}

function ClientsAdvertisersForm(props: ClientsAdvertisersFormProps) {
  const { apiHost, apiHostFor } = useConfig();
  const { showSnackBar } = useSnackBar();

  // Advertisers show opts into the per-domain flip (ACH-colk); the write paths
  // (PUT/POST in onSubmit) stay on the plain Rails apiHost — writes are out of
  // the read-surface flip scope.
  const advertisersHost = apiHostFor('advertisers');

  const [managersList, setManagersList] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [traffickersList, setTraffickersList] = useState([]);

  const { id: advertiserId, clientId: clientIdProp } = props;

  const isEdit = Boolean(advertiserId);

  const form = useForm<ClientsAdvertisersFormInputs>({
    defaultValues: {
      ...defaultValues,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
  } = form;
  const navigate = useNavigate();

  const clientId = clientIdProp || getValues('clientId');

  useConfirmNavChange(() => isDirty);

  const onSubmit = async (fields) => {
    const payload = normalizePayload(clientId, fields);
    let response;
    if (isEdit) {
      response = await putRequestJSON(
        apiHost,
        generatePath(API_ADVERTISERS_EDIT_URL, { id: advertiserId.toString() }),
        payload
      );
    } else {
      response = await postRequestJSON(
        apiHost,
        generatePath(API_CLIENTS_ADVERTISERS_CREATE_URL, { id: clientId.toString() }),
        payload
      );
    }

    // The server can return `error` or `errors` or `exception`
    let errors = get(response, 'errors');
    if (!errors) {
      errors = get(response, 'error');
    }
    if (!errors) {
      errors = get(response, 'exception');
    }

    if (errors) {
      let messages;
      // `errors` can be an array of strings, or an array.
      if (Array.isArray(errors)) {
        messages = errors;
      } else {
        messages = [errors];
      }
      messages.forEach((message) => {
        showSnackBar(message, 'error', { event: 'advertiser_save_error' });
      });
      return;
    }

    const newAdvertiserId = get(response, 'model.id');
    showSnackBar('Successfully saved', 'success', {
      event: isEdit ? 'advertiser_updated' : 'advertiser_created',
      advertiser_id: isEdit ? advertiserId : newAdvertiserId,
      client_id: clientId,
    });
    reset(normalizeForm(response?.model));

    // If we are creating a client, then go to the edit client URL on success.
    if (!isEdit && newAdvertiserId) {
      setTimeout(() => {
        navigate(
          generatePath(CLIENTS_ADVERTISERS_EDIT_URL, {
            id: clientId.toString(),
            advertiserId: newAdvertiserId.toString(),
          })
        );
      }, 1000);
    }
  };

  useEffect(() => {
    if (isEdit) {
      getRequestJSON(advertisersHost, generatePath(API_ADVERTISERS_EDIT_URL, { id: advertiserId.toString() }))
        // ACH wraps the show in the D2 {model} envelope (RD-2); unwrap back to
        // the bare advertiser this form expects — a no-op on Rails.
        .then((raw) => unwrapAchShowModel(raw, isAchHost(advertisersHost)))
        .then((response) => {
          if (response.error) {
            showSnackBar(response.error, 'error', {
              event: 'advertiser_load_error',
              advertiser_id: advertiserId,
              client_id: clientId,
            });
            return;
          }

          if (response.id) {
            const formValues = normalizeForm(response);
            reset(formValues);
          }
        });
    }
  }, [isEdit, advertiserId, advertisersHost, reset, showSnackBar, clientId]);

  // The user role lists opt into the per-domain flip (ACH-colk). Rails renders
  // them as a BARE array; ACH wraps them in the SPEC §2 {collection} envelope
  // (user/map.go MapUserList) — unwrap back to the array this form maps over.
  const usersHost = apiHostFor('users');

  useEffect(() => {
    const isAchUsers = isAchHost(usersHost);

    const getManagers = async () => {
      const response = await getRequestJSON(usersHost, API_USERS_MANAGERS_LIST_URL);
      setManagersList(unwrapAchBareCollection(response, isAchUsers));
    };

    const getSales = async () => {
      const response = await getRequestJSON(usersHost, API_USERS_SALES_LIST_URL);
      setSalesList(unwrapAchBareCollection(response, isAchUsers));
    };

    const getTraffickers = async () => {
      const response = await getRequestJSON(usersHost, API_USERS_TRAFFICKER_LIST_URL);
      setTraffickersList(unwrapAchBareCollection(response, isAchUsers));
    };

    getManagers();
    getSales();
    getTraffickers();
  }, [usersHost]);

  return (
    <FormProvider {...form}>
      <Stack spacing={2} p={2}>
        <Stack>
          <Typography variant="h5">
            {isEdit ? 'Edit' : 'Create'} Advertiser {isEdit && `ID ${advertiserId}`}
          </Typography>
        </Stack>
        <Stack spacing={2} p={2} sx={{ background: 'white', borderRadius: '5px' }}>
          <Typography variant="h6">General</Typography>
          <Stack>
            <TextField
              name="name"
              label="Name"
              rules={{
                required: 'Please enter a name.',
              }}
              fullWidth
              disabled
            />
          </Stack>
          <Stack direction="row" spacing={2} width={1}>
            <Select
              name="managerId"
              label="Account Manager"
              options={managersList.map(({ id: value, name: label }) => ({ value, label }))}
              rules={{
                required: 'Please select an account manager.',
              }}
              fullWidth
              disabled
            />
            <Select
              name="salesRepId"
              label="Sales Manager"
              options={salesList.map(({ id: value, name: label }) => ({ value, label }))}
              rules={{
                required: 'Please select a sales manager.',
              }}
              fullWidth
              disabled
            />
            <Select
              name="traffickerId"
              label="Trafficker"
              options={traffickersList.map(({ id: value, name: label }) => ({ value, label }))}
              rules={{
                required: 'Please select a trafficker.',
              }}
              fullWidth
              disabled
            />
          </Stack>
          <Category disabled />
          <Stack>
            <Switch name="mapsEnabled" label="Dashboard Maps" />
          </Stack>
        </Stack>
        {isEdit && (
          <Stack mt={2} p={2} sx={{ background: 'white', borderRadius: '5px' }}>
            <CreativesList advertiserId={advertiserId} />
          </Stack>
        )}
        <FormEndOptions position="fixed">
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
              Save Advertiser
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default ClientsAdvertisersForm;
